import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #transition = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.details.addEventListener('toggle', this.#onNativeToggle);
    document.addEventListener('dialog:open', this.#onOtherDialogOpen, true);
    window.addEventListener('resize', this.#syncPanelGeometry, { passive: true });
    this.#setupAnimatedElementListeners();
    this.#prepareCollectionLinks();
  }

  disconnectedCallback() {
    this.#transition++;
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#onNativeToggle);
    document.removeEventListener('dialog:open', this.#onOtherDialogOpen, true);
    window.removeEventListener('resize', this.#syncPanelGeometry);
    document.body.classList.remove('vs-menu-drawer-open');
    this.#setTopRowMenuOpenStyle(false);
  }

  #setTopRowMenuOpenStyle(isOpen) {
    const row = document.querySelector('#header-component .header__row--top');
    if (!row) return;
    if (isOpen) {
      row.classList.add('vs-header-top-row--menu-open');
      row.style.setProperty('background', '#ffffff', 'important');
      row.style.setProperty('background-color', '#ffffff', 'important');
      row.style.setProperty('background-image', 'none', 'important');
      row.style.setProperty('backdrop-filter', 'none', 'important');
      row.style.setProperty('-webkit-backdrop-filter', 'none', 'important');
      row.style.setProperty('border-radius', '24px 24px 0 0', 'important');
      row.style.setProperty('border', '1px solid rgba(11, 49, 152, 0.12)', 'important');
      row.style.setProperty('border-bottom', '0', 'important');
      row.style.setProperty('box-shadow', 'none', 'important');
    } else {
      row.classList.remove('vs-header-top-row--menu-open');
      row.style.removeProperty('background');
      row.style.removeProperty('background-color');
      row.style.removeProperty('background-image');
      row.style.removeProperty('backdrop-filter');
      row.style.removeProperty('-webkit-backdrop-filter');
      row.style.removeProperty('border-radius');
      row.style.removeProperty('border');
      row.style.removeProperty('border-bottom');
      row.style.removeProperty('border-bottom-width');
      row.style.removeProperty('border-color');
      row.style.removeProperty('box-shadow');
    }
  }

  // The mobile drawer is a continuation of the liquid header, forming a unified rounded card.
  // Measure the actual header to align width and insets seamlessly.
  #syncPanelGeometry = () => {
    if (!this.isOpen) return;
    const row = document.querySelector('#header-component .header__row--top');
    if (!row) return;
    const box = row.getBoundingClientRect();
    const containingBlock = this.refs.menuDrawer.offsetParent?.getBoundingClientRect();
    const originTop = containingBlock?.top ?? 0;
    const root = document.documentElement.style;
    const inset = Math.max(0, Math.round(box.left));
    root.setProperty('--vs-menu-panel-top', `${Math.max(0, Math.round(box.bottom - originTop))}px`);
    root.setProperty('--vs-menu-panel-left', `${inset}px`);
    root.setProperty('--vs-menu-panel-width', `${Math.round(box.width)}px`);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  // A search/cart dialog owns focus and scroll while open. Close the menu
  // synchronously so a delayed drawer animation cannot reclaim its focus.
  #onOtherDialogOpen = () => {
    if (this.isOpen) this.#close(this.refs.details, true);
  };

  /**
   * Safari and assistive input can toggle the native details element without
   * reaching the declarative click handler. Reconcile that native state on the
   * next frame so the drawer class, animation and body scroll lock stay in sync.
   */
  #onNativeToggle = () => {
    if (this.refs.details.open) {
      document.body.classList.add('vs-menu-drawer-open');
      this.#setTopRowMenuOpenStyle(true);
      if (!this.refs.details.classList.contains('menu-open')) {
        this.open();
      }
    } else {
      document.body.classList.remove('vs-menu-drawer-open');
      this.#setTopRowMenuOpenStyle(false);
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Own this activation. With reduced motion the close callback can reset
    // <details> before the native summary default action, reopening it again.
    event?.preventDefault();
    const summary = this.refs.details.querySelector('summary');
    if (summary?.getAttribute('aria-expanded') === 'true') return this.close();
    this.refs.details.open = true;
    document.body.classList.add('vs-menu-drawer-open');
    this.#setTopRowMenuOpenStyle(true);
    return this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    const transition = ++this.#transition;

    if (details === this.refs.details) {
      document.body.classList.add('vs-menu-drawer-open');
      this.#setTopRowMenuOpenStyle(true);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (transition !== this.#transition || !this.isConnected) return;
      this.#syncPanelGeometry();
      details.classList.add('menu-open');

      if (details === this.refs.details) {
        document.body.classList.add('vs-menu-drawer-open');
        this.#setTopRowMenuOpenStyle(true);
      }

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        if (transition === this.#transition && details.open) trapFocus(details);
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   * @param {boolean} [immediate] Release the menu before another dialog takes over.
   */
  #close(details, immediate = false) {
    const summary = details.querySelector('summary');

    if (!summary) return;
    const transition = ++this.#transition;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (details === this.refs.details) {
      document.body.classList.remove('vs-menu-drawer-open');
      this.#setTopRowMenuOpenStyle(false);
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    const finish = () => {
      if (transition !== this.#transition) return;
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };
    if (immediate) finish();
    else onAnimationEnd(drawer || details, finish, { subtree: false });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Collection archive separates available products from sold pieces. Make
   * navigation land on purchasable items first, without changing menu data in
   * Shopify or adding a client-side redirect after the next page has loaded.
   */
  #prepareCollectionLinks() {
    this.querySelectorAll('a[href]').forEach((anchor) => {
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/collections/')) return;
      url.searchParams.set('filter.v.availability', '1');
      if (!url.searchParams.has('sort_by')) url.searchParams.set('sort_by', 'created-descending');
      anchor.href = url.toString();
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
